import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TrendingUp,
  DollarSign,
  Eye,
  Target,
  ChevronRight, 
  Home, BarChart3, ShoppingCart, User,
  Sparkles,
} from "lucide-react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
} from "recharts";

export default function DashboardMobileModern() {
  const [month, setMonth] = useState("ALL");

  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [spiData, setSpiData] = useState([]);
  const [programData, setProgramData] = useState([]);

  const [tab, setTab] = useState("overview");

  const token = () => localStorage.getItem("token");
  const kode_toko = () => localStorage.getItem("kode_toko");

  const navItems = [
  {
    label: "Home",
    icon: Home,
    path: "/m-home",
  },
  {
    label: "Analytics",
    icon: BarChart3,
    path: "/m-analytics",
  },
  {
    label: "Sales",
    icon: ShoppingCart,
    path: "/m-sales",
  },
  {
    label: "Profile",
    icon: User,
    path: "/m-profile",
  },
];

const navigate = useNavigate();
const location = useLocation();
const isActive = (path) => location.pathname.startsWith(path);

  const formatRupiah = (num) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(num || 0);

  const getMonthFormat = (m) => {
    if (m === "ALL") return "ALL";
    const year = new Date().getFullYear();
    return `${year}-${String(m).padStart(2, "0")}`;
  };

  /* ================= FETCH SUMMARY ================= */
  const fetchSummary = async () => {
    try {
      const res = await fetch(
        `/api/sales/summary-monthly?bulan=${getMonthFormat(
          month
        )}&kode_toko=${kode_toko()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();
      const row = json.data?.[0];

      setSummary({
        net_revenue: Number(row?.net_revenue || 0),
        gross_revenue: Number(row?.gross_revenue || 0),
        total_transaksi: Number(row?.total_transaksi || 0),
        flow_in: Number(row?.flow_in || 0),
        ctr: Number(row?.ctr_percent || 0),
      });
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= FETCH CHART ================= */
  const fetchChart = async () => {
    try {
      const res = await fetch(
        `/api/sales/chart-overview?bulan=${getMonthFormat(
          month
        )}&kode_toko=${kode_toko()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();

      setChartData(
        (json.data || []).map((i) => ({
          date: i.date?.split("T")[0],
          net: Number(i.net_revenue),
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= FETCH SPI ================= */
  const fetchSpi = async () => {
    try {
      const res = await fetch(
        `/api/sales/summary-spi?bulan=${getMonthFormat(
          month
        )}&kode_toko=${kode_toko()}&limit=5`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();

      setSpiData(
        (json.data || []).map((i) => ({
          name: i.crew,
          revenue: Number(i.net_revenue),
          rank: i.ranking,
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= FETCH PROGRAM ================= */
  const fetchProgram = async () => {
    try {
      const res = await fetch(
        `/api/sales/summary-by-program?bulan=${getMonthFormat(
          month
        )}&kode_toko=${kode_toko()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();

      setProgramData(
        (json.data || []).map((i) => ({
          name: i.program_promo,
          revenue: Number(i.net_revenue),
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchChart();
    fetchSpi();
    fetchProgram();
  }, [month]);

  const stats = [
    {
      title: "Net Revenue",
      value: formatRupiah(summary?.net_revenue),
      icon: DollarSign,
      color: "from-indigo-500 to-blue-500",
    },
    {
      title: "Transactions",
      value: summary?.total_transaksi || 0,
      icon: TrendingUp,
      color: "from-emerald-500 to-green-500",
    },
    {
      title: "Visitors",
      value: summary?.flow_in || 0,
      icon: Eye,
      color: "from-orange-500 to-amber-500",
    },
    {
      title: "CTR",
      value: `${summary?.ctr?.toFixed(1) || 0}%`,
      icon: Target,
      color: "from-pink-500 to-rose-500",
    },
  ];

  return (
   <div className="min-h-screen bg-[#f4f7fb] md:hidden pb-24 overflow-x-hidden">

      {/* HEADER */}
     <div className="px-2 pt-5">

        <div className="flex items-center justify-between">

          <div>
           

            <h1 className="text-2xl font-bold text-gray-800">
              Dashboard
            </h1>
          </div>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-white border border-gray-200 rounded-xl px-2 py-2 text-xs shadow-sm"
          >
            <option value="ALL">All Months</option>

            {[...Array(12)].map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(0, i).toLocaleString("id-ID", {
                  month: "long",
                })}
              </option>
            ))}
          </select>

        </div>

      </div>

      {/* HERO CARD */}
      <div className="px-2 mt-4">

        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 p-5 text-white shadow-xl">

          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />

          <div className="absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-white/10" />

          <div className="relative z-10">

            <div className="flex items-center gap-2 text-xs text-indigo-100">
              <Sparkles size={14} />
              Monthly Revenue
            </div>

            <h2 className="mt-3 text-3xl font-bold tracking-tight">
              {formatRupiah(summary?.net_revenue)}
            </h2>

            <div className="mt-4 flex items-center gap-2 text-sm">
              <div className="rounded-full bg-white/20 px-2 py-1">
                {summary?.total_transaksi || 0} trx
              </div>

              <div className="rounded-full bg-white/20 px-2 py-1">
                CTR {summary?.ctr?.toFixed(1) || 0}%
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 gap-2.5 px-2 mt-4">

        {stats.map((item, i) => {
          const Icon = item.icon;

          return (
            <div
              key={i}
              className="bg-white rounded-2xl p-3.5 shadow-sm border border-gray-100"
            >

              <div
                className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white shadow-lg`}
              >
                <Icon size={20} />
              </div>

              <p className="text-xs text-gray-400 mt-3">
                {item.title}
              </p>

              <h3 className="mt-1 font-bold text-gray-800 text-sm leading-tight">
                {item.value}
              </h3>

            </div>
          );
        })}

      </div>

      {/* TABS */}
     <div className="px-2 mt-5">

        <div className="bg-white rounded-2xl p-1 flex shadow-sm border">

          {["overview", "SPI", "program"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${
                tab === t
                  ? "bg-indigo-600 text-white shadow"
                  : "text-gray-500"
              }`}
            >
              {t}
            </button>
          ))}

        </div>

      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="px-2 mt-5">

          <div className="bg-white rounded-[28px] p-3 shadow-sm border border-gray-100">

            <div className="flex items-center justify-between mb-4">

              <div>
                <h3 className="font-semibold text-gray-800">
                  Revenue Trend
                </h3>

                <p className="text-xs text-gray-400">
                  Daily analytics
                </p>
              </div>

              <ChevronRight
                size={18}
                className="text-gray-400"
              />

            </div>

            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient
                    id="colorUv"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="#4f46e5"
                      stopOpacity={0.4}
                    />
                    <stop
                      offset="95%"
                      stopColor="#4f46e5"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>

                <XAxis hide dataKey="date" />

                <Tooltip />

                <Area
                  type="monotone"
                  dataKey="net"
                  stroke="#4f46e5"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorUv)"
                />
              </AreaChart>
            </ResponsiveContainer>

          </div>

        </div>
      )}

      {/* SALES */}
      {tab === "SPI" && (
        <div className="px-2 mt-5 space-y-3">

          {spiData.map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-3.5 shadow-sm border flex items-center justify-between"
            >

              <div className="flex items-center gap-3">

                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 text-white flex items-center justify-center font-bold">
                  #{item.rank}
                </div>

                <div>
                  <h3 className="font-semibold text-sm text-gray-800">
                    {item.name}
                  </h3>

                  <p className="text-xs text-gray-400">
                    Top Sales Performance
                  </p>
                </div>

              </div>

              <div className="text-right">
                <p className="font-bold text-sm text-gray-800">
                  Rp {item.revenue.toLocaleString("id-ID")}
                </p>

                <p className="text-xs text-green-500">
                  +12%
                </p>
              </div>

            </div>
          ))}

        </div>
      )}

      {/* PROGRAM */}
      {tab === "program" && (
        <div className="px-2 mt-5 space-y-3">

          {programData.map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-3.5 shadow-sm border"
            >

              <div className="flex justify-between items-center">

                <div>
                  <h3 className="font-semibold text-sm text-gray-800">
                    {item.name}
                  </h3>

                  <p className="text-xs text-gray-400">
                    Program Campaign
                  </p>
                </div>

                <p className="font-bold text-indigo-600 text-sm">
                  Rp {item.revenue.toLocaleString("id-ID")}
                </p>

              </div>

              <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">

                <div
                  className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-500"
                  style={{
                    width: `${
                      (item.revenue /
                        (programData[0]?.revenue || 1)) *
                      100
                    }%`,
                  }}
                />

              </div>

            </div>
          ))}

        </div>
      )}



    </div>
  );
}